import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { CreateGivingLinkDialog } from '@/components/payments/create-giving-link-dialog';
import { GivingLinksPanel } from '@/components/payments/giving-links-panel';
import { OnlineAttemptsPanel } from '@/components/payments/online-attempts-panel';
import { PageHeader } from '@/components/shared/page-header';
import { ShareDialog } from '@/components/shared/share-dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePaymentConnection } from '@/lib/payment-connection';
import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import {
  PERMISSIONS,
  hasPermission,
  isAdminOrOwner,
  useCurrentMembership,
} from '@/lib/permissions';
import { buildGivingLinkUrl } from '@/lib/public-origins';
import { useChurchStore } from '@/stores/church-store';

const PAYMENTS_TABS = ['gifts', 'tithes', 'links'] as const;
type PaymentsTab = (typeof PAYMENTS_TABS)[number];

const isPaymentsTab = (value: string | null): value is PaymentsTab =>
  value !== null && PAYMENTS_TABS.includes(value as PaymentsTab);

export const PaymentsPage = () => {
  const { t } = useTranslation('payments');
  const { t: tCommon } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const canManageLinks = hasPermission(membership, PERMISSIONS.PAYMENTS_MANAGE_LINKS);
  const canConfigurePayments =
    isAdminOrOwner(membership) || hasPermission(membership, PERMISSIONS.PAYMENTS_CONFIGURE);
  const { isReady: paymentReady } = usePaymentConnection();

  const tabFromUrl = searchParams.get('tab');
  const resolvedTab: PaymentsTab = isPaymentsTab(tabFromUrl) ? tabFromUrl : 'gifts';

  const [tab, setTab] = useState<PaymentsTab>(resolvedTab);

  useEffect(() => {
    setTab(resolvedTab);
  }, [resolvedTab]);

  const [createLinkOpen, setCreateLinkOpen] = useState(false);
  const [shareLink, setShareLink] = useState<{ slug: string; title: string } | null>(null);

  useEffect(() => {
    if (searchParams.get('createLink') !== '1') return;
    setTab('links');
    if (paymentReady) {
      setCreateLinkOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('createLink');
    setSearchParams(next, { replace: true });
  }, [paymentReady, searchParams, setSearchParams]);

  const handleTabChange = (nextTab: string) => {
    if (!isPaymentsTab(nextTab)) return;

    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'gifts') {
      next.delete('tab');
    } else {
      next.set('tab', nextTab);
    }
    setSearchParams(next, { replace: true });
  };

  const openCreateLink = () => {
    if (!paymentReady) return;
    setCreateLinkOpen(true);
  };

  const headerActions = canManageLinks ? (
    paymentReady ? (
      <Button onClick={openCreateLink}>{t('links.create')}</Button>
    ) : canConfigurePayments ? (
      <Button asChild>
        <Link to={ONLINE_GIVING_SETTINGS_PATH}>{t('links.requiresConnectionAction')}</Link>
      </Button>
    ) : null
  ) : null;

  return (
    <div>
      <PageHeader title={t('title')} description={t('description')} headerActions={headerActions} />

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="gifts">{t('attempts.title')}</TabsTrigger>
          <TabsTrigger value="tithes">{t('tithes.title')}</TabsTrigger>
          <TabsTrigger value="links">{t('links.title')}</TabsTrigger>
        </TabsList>

        <TabsContent value="gifts" className="mt-4">
          <OnlineAttemptsPanel labelsKey="attempts" showLinkInsights />
        </TabsContent>

        <TabsContent value="tithes" className="mt-4">
          <OnlineAttemptsPanel contributionType="tithe" labelsKey="tithes" />
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          <GivingLinksPanel />
        </TabsContent>
      </Tabs>

      <CreateGivingLinkDialog
        open={createLinkOpen}
        onOpenChange={setCreateLinkOpen}
        onCreated={(link) => {
          setTab('links');
          const next = new URLSearchParams(searchParams);
          next.set('tab', 'links');
          setSearchParams(next, { replace: true });
          setShareLink({ slug: link.slug, title: link.title });
        }}
      />

      <ShareDialog
        open={Boolean(shareLink)}
        onOpenChange={(open) => {
          if (!open) setShareLink(null);
        }}
        url={
          shareLink && church?.slug
            ? buildGivingLinkUrl(church.slug, shareLink.slug)
            : ''
        }
        title={t('links.shareTitle')}
        description={t('links.shareDescription')}
        downloadName={shareLink?.slug ?? 'giving-link'}
        shareTitle={shareLink?.title}
        branding={
          church
            ? {
                churchName: church.name,
                campusName: church.campusName,
                logoUrl: church.logoUrl,
              }
            : undefined
        }
        labels={{
          copy: tCommon('action.copy'),
          copied: tCommon('action.copied'),
          share: t('links.share'),
          download: t('links.downloadQr'),
          close: tCommon('action.close'),
          qrLabel: t('links.qrLabel'),
        }}
      />
    </div>
  );
};
